import { proxy } from 'valtio';
import { proxySet } from 'valtio/utils';

/**
 * Module-level saving-state store.
 *
 * Single source of truth for "is anyone saving?" and "did any save fail?".
 * Replaces the old `<PendingSavesProvider>` whose `useState` rerendered the
 * entire app tree on every save dirty/cleanup, and the per-field `isSaving`
 * state that rerendered each editing field. Mutations to this proxy only notify
 * components that explicitly `useSnapshot(pendingSavesState)` — currently just
 * the navbar saving indicator. `NavigationGuard` reads the raw proxy at
 * event time instead, so per-keystroke churn never re-renders the app root.
 *
 * Both fields are keyed by the same per-save `pendingId`:
 * - `pendingIds` holds one id per in-flight save (added when a field goes dirty,
 *   removed when its write settles). Consumers derive "saving" as
 *   `pendingIds.size > 0`.
 * - `failedSaveIds` holds one id per save whose last write failed. It is a Set —
 *   not a boolean or a message — for the same reason as `pendingIds`: saves are
 *   concurrent and a shared scalar toggled by independent finishers would race,
 *   and one field's success must not clear another field's failure. Consumers
 *   derive "save failed" as `failedSaveIds.size > 0`.
 *
 * Error *messages* are not stored here — they are owned by `MessageProvider`
 * (`handleFirestoreWriteError` logs + toasts). This store only carries the
 * persistence-scoped flag that drives the ambient indicator, so it is never
 * mistaken for a general error bus. An id is flagged failed via `markFailed`
 * (called only from the single persistence-error handler) and cleared on the
 * next edit (`addPending`), a later success, an explicit `clearFailed` (e.g.
 * a Revert that resolves edits without a write), or unmount.
 *
 * - `dirtyIds` holds one id per surface with unsaved edits that have not yet
 *   started saving (explicit save/revert forms, not autosaved fields). It blocks
 *   navigation via `NavigationGuard` but is intentionally *not* read by the navbar
 *   `SavingIndicator` — editing is not "saving", so it must not show "Saving…".
 * - `lastSavedAt` is the epoch-ms of the last fully-clean drain: no in-flight
 *   saves, no failures, and no dirty (unsaved) surfaces. `SavingIndicator` renders
 *   it as "Saved HH:MM:SS". Gated on all three sets so neither a failure nor a
 *   still-dirty explicit-save form is ever misreported as "Saved"; a `⌘S` flush
 *   therefore stamps immediately only when nothing is pending or dirty, otherwise
 *   the stamp lands later on the clean drain.
 *
 * `flushers` is a plain (non-proxied) Set of `debounce.flush` callbacks so `⌘S`
 * (`flushAll`) can force every pending autosave to write immediately. Functions
 * are kept out of the proxy so Valtio never deep-proxies them.
 */
export type PendingSavesState = {
    pendingIds: Set<string>;
    failedSaveIds: Set<string>;
    dirtyIds: Set<string>;
    lastSavedAt: number | null;
};

export const pendingSavesState: PendingSavesState = proxy<PendingSavesState>({
    pendingIds: proxySet<string>(),
    failedSaveIds: proxySet<string>(),
    dirtyIds: proxySet<string>(),
    lastSavedAt: null,
});

const flushers = new Set<() => void>();

const stampSavedIfClean = () => {
    if (
        pendingSavesState.pendingIds.size === 0 &&
        pendingSavesState.failedSaveIds.size === 0 &&
        pendingSavesState.dirtyIds.size === 0
    ) {
        pendingSavesState.lastSavedAt = Date.now();
    }
};

const addPending = (id: string) => {
    pendingSavesState.pendingIds.add(id);
    pendingSavesState.failedSaveIds.delete(id);
};

const removePending = (id: string) => {
    pendingSavesState.pendingIds.delete(id);
    stampSavedIfClean();
};

const registerFlusher = (flush: () => void): (() => void) => {
    flushers.add(flush);
    return () => {
        flushers.delete(flush);
    };
};

const flushAll = () => {
    for (const flush of flushers) {
        flush();
    }
    stampSavedIfClean();
};

const markFailed = (id: string) => {
    pendingSavesState.failedSaveIds.add(id);
};

const clearFailed = (id: string) => {
    pendingSavesState.failedSaveIds.delete(id);
};

const setDirty = (id: string) => {
    pendingSavesState.dirtyIds.add(id);
};

const clearDirty = (id: string) => {
    pendingSavesState.dirtyIds.delete(id);
};

export const pendingSavesActions = Object.freeze({
    addPending,
    removePending,
    markFailed,
    clearFailed,
    setDirty,
    clearDirty,
    registerFlusher,
    flushAll,
});

export type PendingSavesActions = typeof pendingSavesActions;
